import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Sparkles, Globe2, TrendingUp } from "lucide-react";
import SectionLabel from "../components/SectionLabel";

const VALUES = [
  ["Accountability", "We own our delivery and adhere to the highest standards."],
  ["Transparency", "We believe in open communication and visible metrics."],
  ["Reliability", "Consistent performance powered by secure infrastructure."],
  ["Continuous Improvement", "Always optimizing through automation and innovation."],
  ["Client-Centricity", "Deeply understanding and delivering on strategic partner goals."],
];

const DIFFERENTIATORS = [
  { icon: ShieldCheck, t: "Enterprise-Grade Governance & Process Discipline" },
  { icon: Sparkles, t: "Automation-First Mindset Applied to Every Workflow" },
  { icon: Globe2, t: "Scalable Hybrid Delivery Models from India Strategic Hubs" },
  { icon: TrendingUp, t: "Commitment to Verifiable Operational Outcomes" },
];

const LEADERS = [
  {
    name: "Sutapa Deb",
    role: "Director – Operations & Governance",
    bio: "Sutapa Deb provides expert oversight in enterprise-grade leadership and governance frameworks. With a disciplined approach to operational structures, she ensures that every workflow aligns with the company's core objectives of credibility and transparency.",
  },
  {
    name: "Gargi Dutta",
    role: "Director – Strategy & Business Development",
    bio: "Gargi Dutta drives the strategic trajectory and market positioning of Operexa Gennex. By focusing on identifying high-value client acquisitions and fostering long-term strategic partnerships, she ensures the company remains at the forefront of the technology services industry.",
  },
  {
    name: "Suvrarup Deb",
    role: "Director – Client Success & Delivery",
    bio: "Suvrarup Deb is dedicated to delivery excellence and measurable client outcomes. Through a partnership-driven growth model, he ensures that service quality and operational precision remain consistent across all project lifecycles.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#050505] text-white">
      <section className="pt-32 pb-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionLabel>About Operexa Gennex</SectionLabel>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-6 leading-[1.05]">
              Built for Operational Excellence.<br />
              Powered by <span className="text-[#3B82F6]">Technology.</span>
            </h1>
            <p className="mt-6 text-zinc-400 max-w-2xl">
              Operexa Gennex serves as a premier global operations and technology partner, bridging the gap between strategy and execution through discipline, innovation, and a focus on enterprise reliability.
            </p>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] border border-white/10 overflow-hidden">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/32e79249-1014-4415-b28c-d04f3efa646b/images/3e2fd6ddd104c82ba2654b7910014ef6163140dd652fbd8b13904fe16674f2f0.png"
                alt="Operexa office"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#3B82F6]/20 to-transparent mix-blend-overlay" />
            </div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 border border-[#3B82F6]/40 -z-10" />
          </div>
        </div>
      </section>

      {/* WHO / MISSION / VISION */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5 border border-white/10">
          {[
            { t: "Who We Are", d: "Strategically headquartered with global delivery hubs in India, we support enterprises and Fortune 500 partners in scaling complex back-office and technology operations. We provide high-governance BPO and KPO services tailored to strictly regulated enterprise environments." },
            { t: "Our Mission", d: "To empower global organizations by delivering operational excellence and measurable business results through high-governance process discipline and scalable technology solutions." },
            { t: "Our Vision", d: "To become the world's most trusted global partner for integrated operations and technology, leading with an automation-first delivery model and enterprise-grade security standards." },
          ].map((c, i) => (
            <div key={c.t} data-testid={`about-block-${i}`} className="bg-[#050505] p-8 lg:p-10">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-4">0{i + 1}</div>
              <h3 className="font-display text-2xl font-semibold mb-4">{c.t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="py-24 border-t border-white/10 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>What Makes Us Different</SectionLabel>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
            {DIFFERENTIATORS.map((d, i) => {
              const Ic = d.icon;
              return (
                <div key={d.t} className="bg-[#08080a] p-8" data-testid={`diff-${i}`}>
                  <Ic className="w-7 h-7 text-[#3B82F6] mb-4" />
                  <p className="font-display text-base font-semibold leading-snug">{d.t}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <SectionLabel>Our Values</SectionLabel>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-5">
              The principles behind every engagement.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <ul className="divide-y divide-white/10 border-y border-white/10">
              {VALUES.map(([k, v], i) => (
                <li key={k} className="grid grid-cols-12 gap-4 py-6 group hover:bg-white/[0.02] transition-colors px-2" data-testid={`value-${i}`}>
                  <div className="col-span-1 font-mono text-xs text-zinc-500">0{i + 1}</div>
                  <div className="col-span-3 font-display text-lg font-semibold text-white">{k}</div>
                  <div className="col-span-8 text-zinc-400">{v}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="py-24 border-t border-white/10 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>Leadership & Governance</SectionLabel>
          <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-5 max-w-3xl">
            Operational since 2026, led by a seasoned leadership team.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/10">
            {LEADERS.map((l, i) => (
              <motion.div
                key={l.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#08080a] p-8 lg:p-10 hover:bg-[#0d0d10] transition-colors group relative"
                data-testid={`leader-${i}`}
              >
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-6">
                  Director · 0{i + 1}
                </div>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 border border-white/15 bg-[#3B82F6]/10 flex items-center justify-center shrink-0 group-hover:border-[#3B82F6] transition-colors">
                    <span className="font-display text-xl font-bold text-[#3B82F6]">
                      {l.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-semibold leading-tight">{l.name}</h3>
                    <div className="text-sm text-zinc-400 mt-1.5">{l.role}</div>
                  </div>
                </div>
                <div className="w-12 h-px bg-[#3B82F6]/40 mb-5" />
                <p className="text-sm text-zinc-400 leading-relaxed">{l.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
